import { Injectable, inject } from '@angular/core';
import { RideBoardListItem } from '../../../shared/interfaces/ride-board-list-item';
import { RideDocumentDto, RideStopDocumentDto } from '../../../shared/interfaces/ride-firestore';
import { MapRideServiceService } from '../../monitoring-center/services/map-ride-service.service';
import { RideDriverExtraFields } from '../../../shared/enums/extraFields';
import { RideStatusDescription, RideStatusType } from '../../../shared/enums/rideStatusType';
import { StatusSeverity } from '../../../shared/custom/statusSeverity';
import { RideStopStatusType, StopArrivalTimeStatus } from '../../../shared/enums/rideStopStatusType';
import { badgeSeverity } from '../../../shared/components/badge/badge.component';

interface StopStatusInfo {
  statusType: string;
  isActive: boolean;
}

interface StopTimeInfo {
  displayTime: string;
}

interface StopProcessingInfo {
  previousStop: string;
  previousStopStatus: string;
  previousStopTimeDiff: number;
  departureStopTime: string;
  departureStopTimeAdjustment: string;
}

interface NextStopInfo {
  nextStop: string;
  arrivalStopTime: string;
  arrivalStopStatus: string;
  arrivalStopTimeAdjustment: string;
  arrivalStopTimeDiff: number;
}

interface StopProcessingContext {
  stop: RideStopDocumentDto;
  statusInfo: StopStatusInfo;
  rideStatus: string;
  stopAddress: string;
  timeInfo: StopTimeInfo;
  dateOfService: string;
  prevOrder: number;
  previousInfo: StopProcessingInfo;
  nextInfo: NextStopInfo;
}

@Injectable({
  providedIn: 'root',
})
export class MapBoardService {
  private readonly mapRideService = inject(MapRideServiceService);

  getMappedItem(ride: RideDocumentDto): RideBoardListItem {
    const driver = ride.ride_drivers?.filter(b => !b.is_deleted)[0];
    const firstName = driver?.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.FirstName)?.value;
    const lastName = driver?.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.LastName)?.value;
    const driverProvider = driver?.ride_driver_extra_fields?.find(rd => rd.key_id == RideDriverExtraFields.OrganizationName)?.value;
    const dateOfService = this.mapRideService.getDateOfService(ride.date_of_service);
    const times = this.mapRideService.getRideTime(ride, false);

    let rideAlertTitle = 'On Time';
    let rideStatus = this.mapRideService.getRideStatus(ride);
    let previousStop = 'N/A';
    let previousStopStatus = '';
    let previousStopTimeDiff = 0;
    let arrivalStopTimeDiff = 0;
    let departureStopTime = 'N/A';
    let departureStopTimeAdjustment = '';

    let nextStop = 'N/A';
    let arrivalStopTime = times.to;
    let arrivalStopStatus = '';
    let arrivalStopTimeAdjustment = '';

    let prevOrder = 0;
    if (ride.ride_stops) {
      let firstStopTime = '';
      let lastVisitedStopTime = '';
      let firstStopAddress = '';

      ({ firstStopTime, firstStopAddress, lastVisitedStopTime } = getStopsStatus(firstStopTime, firstStopAddress, lastVisitedStopTime, ride, this.mapRideService));
      if (nextStop == 'N/A' && rideStatus != RideStatusDescription(RideStatusType.NoShow) && rideStatus != RideStatusDescription(RideStatusType.Completed)) {
        nextStop = firstStopAddress;
        arrivalStopTime = firstStopTime;
      }
      if (rideStatus == RideStatusDescription(RideStatusType.NoShow) || rideStatus == RideStatusDescription(RideStatusType.Completed)) {
        arrivalStopTime = lastVisitedStopTime;
      }
    }
    if (rideStatus.includes('2X')) {
      rideStatus = '2X Booked';
    }

    let rideAlertColor: StatusSeverity = 'info';

    function getAlertIconBasedOnTime() {
      if (previousStopTimeDiff >= -1 && previousStopTimeDiff <= 1 && arrivalStopTimeDiff >= -1 && arrivalStopTimeDiff <= 1) {
        rideAlertColor = 'info';
      } else if ((previousStopTimeDiff > 1 && previousStopTimeDiff <= 5) || (arrivalStopTimeDiff > 1 && arrivalStopTimeDiff <= 5)) {
        rideAlertColor = 'warning';
      } else if (previousStopTimeDiff > 5 || arrivalStopTimeDiff > 5) {
        rideAlertColor = 'danger';
      }
    }

    const getAlertIcon = () => {
      if (rideStatus == RideStatusDescription(RideStatusType.Declined)) {
        rideAlertTitle = RideStatusDescription(RideStatusType.Declined);
        rideAlertColor = 'danger';
      } else if (rideStatus == RideStatusDescription(RideStatusType.InProgress) || rideStatus == RideStatusDescription(RideStatusType.Completed) || rideStatus == RideStatusDescription(RideStatusType.NoShow)) {
        if (previousStopStatus == 'late' || arrivalStopStatus == 'late') {
          rideAlertTitle = 'Late';
          //check if diff
          // -1 to 1 minute should be in white
          // Late by more than 5 minutes should be in red
          // Less than 5 minutes and more than 1 minute should be in orange

          getAlertIconBasedOnTime();
        } else if (previousStopStatus == 'early' || arrivalStopStatus == 'early') {
          // Arriving more than 1 minute earlier should be in green
          rideAlertTitle = 'Early';
          rideAlertColor = 'success';
        } else {
          rideAlertColor = 'info';
        }
      } else if (rideStatus == RideStatusDescription(RideStatusType.Accepted) || rideStatus == 'Scheduled' || rideStatus == RideStatusDescription(RideStatusType.Offered)) {
        rideAlertColor = this.mapRideService.statusSeverity;
        if (rideAlertColor == 'danger') {
          rideAlertColor = 'warning';
          rideAlertTitle = 'Late';
        }
        if (rideAlertColor == 'success') rideAlertTitle = 'Early';
      } else if (rideStatus == RideStatusDescription(RideStatusType.CanceledDueToHoliday) || rideStatus == RideStatusDescription(RideStatusType.CanceledInAdvance) || rideStatus == RideStatusDescription(RideStatusType.CanceledLate)) {
        rideAlertTitle = 'Canceled';
        rideAlertColor = 'secondary';
      }
    };
    getAlertIcon();

    if (rideAlertTitle == 'On Time' && this.mapRideService.statusSeverity == 'danger') {
      rideAlertTitle = 'Attention';
      rideAlertColor = 'danger';
    }

    return {
      rideId: ride.original_ride_id,
      rideOrder: ride.ride_order,
      status: rideStatus,
      statusSeverity: this.mapRideService.statusSeverity,
      providerName: driverProvider,
      driverName: `${firstName} ${lastName}`,
      studentNames: this.mapRideService.getStudentNames(ride),
      departureTime: { time: departureStopTime, timeAdjustment: departureStopTimeAdjustment },
      arrivalTime: { time: arrivalStopTime, timeAdjustment: arrivalStopTimeAdjustment },
      previousStop: previousStop,
      nextStop: nextStop,
      rideAlert: { title: rideAlertTitle, color: rideAlertColor },
    } as RideBoardListItem;

    function getStopsStatus(firstStopTime: string, firstStopAddress: string, lastVisitedStopTime: string, ride: RideDocumentDto, mapRideService: MapRideServiceService) {
      for (const stop of ride.ride_stops!) {
        const statusInfo = mapRideService.determineStopStatus(stop);
        const timeInfo = mapRideService.calculateStopTimes(stop, ride, statusInfo.statusType, false);
        const stopAddress = mapRideService.formatStopAddress(stop);
        if (stop.order == 1) {
          firstStopTime = timeInfo.displayTime;
          firstStopAddress = stopAddress;
        }
        lastVisitedStopTime = getStopStatus(statusInfo, lastVisitedStopTime, timeInfo, stop, stopAddress, mapRideService);

        if (statusInfo.isActive) {
          nextStop = stopAddress;
          arrivalStopTime = timeInfo.displayTime;
          const rideDiff = getStopRideDiff(stop, dateOfService, mapRideService);
          const timeDiff = Number(rideDiff.timeDiff) < 0 ? Math.ceil(Number(rideDiff.timeDiff)) : Math.floor(Number(rideDiff.timeDiff));
          if (timeDiff != 0) arrivalStopTimeAdjustment = timeDiff > 0 ? '+' + timeDiff.toString() : timeDiff.toString();
          arrivalStopStatus = rideDiff.arrivalStatus;
          arrivalStopTimeDiff = timeDiff;
        }
      }
      return { firstStopTime, firstStopAddress, lastVisitedStopTime };
    }
    function getStopStatus(
      statusInfo: { statusType: RideStopStatusType; color: badgeSeverity; isActive: boolean },
      lastVisitedStopTime: string,
      timeInfo: { displayTime: string; scheduledArrivalTime: string; estimatedArrivalTime: string; actualArrivalTime: string; color?: badgeSeverity; arrivalStatus: StopArrivalTimeStatus; statusDescription: string },
      stop: RideStopDocumentDto,
      stopAddress: string,
      mapRideService: MapRideServiceService,
    ) {
      if (statusInfo.statusType == 'Visited') {
        lastVisitedStopTime = timeInfo.displayTime;
      }
      if (stop.order! > prevOrder && (statusInfo.statusType == 'Arrived' || statusInfo.statusType == 'Visited')) {
        if (!statusInfo.isActive && rideStatus != RideStatusDescription(RideStatusType.None) && rideStatus != RideStatusDescription(RideStatusType.Offered) && rideStatus != RideStatusDescription(RideStatusType.Accepted)) {
          previousStop = stopAddress;
          if (statusInfo.statusType == 'Visited' && rideStatus == RideStatusDescription(RideStatusType.InProgress)) {
            departureStopTime = timeInfo.displayTime;
          }
        }

        getStopStatusInactive();
        prevOrder = stop.order!;
      }
      return lastVisitedStopTime;

      function getStopStatusInactive() {
        if (!statusInfo.isActive) {
          const rideDiff = getStopRideDiff(stop, dateOfService, mapRideService);
          const timeDiff = Number(rideDiff.timeDiff) < 0 ? Math.ceil(Number(rideDiff.timeDiff)) : Math.floor(Number(rideDiff.timeDiff));

          if (timeDiff != 0) departureStopTimeAdjustment = timeDiff > 0 ? '+' + timeDiff.toString() : timeDiff.toString();
          previousStopStatus = rideDiff.arrivalStatus;
          previousStopTimeDiff = timeDiff;
        }
      }
    }
  }
}

function getStopRideDiff(stop: RideStopDocumentDto, dateOfService: string, mapRideService: MapRideServiceService) {
  const scheduledAndActualArrivalTimeDifferenceInSeconds = mapRideService.getDifferenceTimeInSeconds(stop.ride_stop_times!.actual_arrival_time_utc, stop.ride_stop_times!.scheduled_arrival_time_utc, dateOfService);
  const diff = !Number.isNaN(scheduledAndActualArrivalTimeDifferenceInSeconds) && scheduledAndActualArrivalTimeDifferenceInSeconds === 0 ? 0 : Math.round(scheduledAndActualArrivalTimeDifferenceInSeconds / 60);

  let arrivalStatus: StopArrivalTimeStatus = 'onTime';

  let timeDiff = '';
  if (diff > 0) {
    timeDiff = `+${diff}`;
    arrivalStatus = 'late';
  } else if (diff < 0) {
    timeDiff = `-${diff * -1}`;
    arrivalStatus = 'early';
  }
  return {
    timeDiff,
    diff,
    arrivalStatus,
  };
}
